import { useRef, useLayoutEffect } from "react";
import "./GSAPSection.module.css"
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Heading } from "../../";

gsap.registerPlugin(ScrollTrigger);

const spanStyle = {
    fontFamily: "Manrope, sans-serif",
    fontSize: "0.9rem",
    fontWeight: 500,
    color: "rgba(224,224,224,0.6)",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: "1rem",
};

const subStyle = {
    fontFamily: "Manrope, sans-serif",
    fontSize: "0.9rem",
    color: "rgba(224,224,224,0.45)",
    letterSpacing: "0.04em",
    marginTop: "1.75rem",
    textAlign: "center",
};

const stepStyle = {
    position: "absolute",
    top: "2rem",
    right: "2.5rem",
    fontFamily: "Manrope, sans-serif",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "rgba(126,96,248,0.7)",
    letterSpacing: "0.18em",
    zIndex: 20,
};

const iconWrapStyle = {
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.1)",
    background: "rgba(255,255,255,0.03)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "2rem",
};

const accentLineStyle = {
    width: 72,
    height: 2,
    borderRadius: 1,
    background: "linear-gradient(90deg, #6ddcff, #7e60f8)",
    marginTop: "1.75rem",
};

const geoStyle = (top, right, bottom, left) => ({
    position: "absolute",
    opacity: 0.045,
    ...(top !== undefined && { top }),
    ...(right !== undefined && { right }),
    ...(bottom !== undefined && { bottom }),
    ...(left !== undefined && { left }),
    pointerEvents: "none",
});

function GeoRing({ size = 280, top, right, bottom, left }) {
    return (
        <svg style={geoStyle(top, right, bottom, left)} width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle cx={size / 2} cy={size / 2} r={size / 2 - 30} fill="none" stroke="#7e60f8" strokeWidth="40" />
            <circle cx={size / 2} cy={size / 2} r={size / 2 - 70} fill="none" stroke="#6ddcff" strokeWidth="16" />
        </svg>
    );
}

function Panel({ step, total, intro, heading, sub, icon, geoPos }) {
    return (
        <div className="gsapsectionV">
            <div className="h-screen p-[200px] md:p-5 bg-black-900_02 relative">

                <span style={stepStyle}>{String(step).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>

                <GeoRing size={320} {...geoPos} />

                <div className="flex flex-col items-center justify-center w-full bottom-[38%] right-0 left-0 px-[120px] m-auto md:px-10 sm:px-6 absolute max-w-[1040px] z-10">
                    <div style={iconWrapStyle}>{icon}</div>
                    <span style={spanStyle}>{intro}</span>
                    <Heading size="6xl" as="h2" className="!text-transparent bg-gradient5 bg-clip-text text-center md:!text-[52px] sm:!text-[36px]">
                        {heading}
                    </Heading>
                    <div style={accentLineStyle} />
                    {sub && <span style={subStyle}>{sub}</span>}
                </div>

                <div className="h-[325px] w-[63%] bottom-[29%] right-0 left-0 m-auto border-black-900_7e border border-solid bg-gradient1 backdrop-opacity-[0.4] blur-[210.00px] absolute" />
            </div>
        </div>
    );
}

const panels = [
    {
        intro: "AI is shifting from tool to",
        heading: "Continuous interaction",
        geoPos: { bottom: -60, right: -60 },
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6ddcff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                <path d="M3 21v-5h5" />
            </svg>
        ),
    },
    {
        intro: "From stateless to",
        heading: "Persistent",
        geoPos: { top: -60, left: -60 },
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#7e60f8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
        ),
    },
    {
        intro: "From neutral to",
        heading: "Emotional",
        geoPos: { bottom: -60, right: -60 },
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6ddcff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12h3l3-9 4 18 3-9h5" />
            </svg>
        ),
    },
    {
        intro: "Value changes from tasks to",
        heading: "Relationship intelligence",
        sub: "Driven by companions, games, assistants, robotics",
        geoPos: { bottom: -40, right: -40 },
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#7e60f8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="5" r="2" />
                <circle cx="5" cy="19" r="2" />
                <circle cx="19" cy="19" r="2" />
                <path d="M12 7v4M12 11l-5.5 6M12 11l5.5 6" />
            </svg>
        ),
    },
];

function GsapSection3() {
    const containerRef = useRef(null);

    useLayoutEffect(() => {
        const ctx = gsap.context(() => {
            gsap.utils.toArray(".gsapsectionV").forEach((panel, i) => {
                ScrollTrigger.create({
                    trigger: panel,
                    start: "top top",
                    end: () => `+=${window.innerHeight}`,
                    pin: true,
                    pinSpacing: false,
                    id: "gsapsectionV" + i,
                });
            });
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <div className="gsapwrapperV bg-black-900_02" ref={containerRef}>
            {panels.map((p, i) => (
                <Panel key={i} step={i + 1} total={panels.length} {...p} />
            ))}
        </div>
    );
}

export default GsapSection3;
